import React from 'react';
import { Calendar, Clock, User } from 'lucide-react';
import example1 from '../../Assets/Images/image-example-2.webp';
import example2 from '../../Assets/Images/image-example-2.webp';
import { Link } from 'react-router-dom';

const BlogTemplate = () => {
    return (
        <article className="max-w-4xl mx-auto p-6 bg-white">
            {/* Header Section */}
            <header className="h-[60vh] mt-40 flex justify-center items-center flex-col text-center">
                <h1 className="text-4xl font-bold text-gray-900 mb-4">
                    How to Create a Business in Dubai with an All-Inclusive Office Lease Agreement
                </h1>
                <div className="flex justify-center gap-6 text-gray-600 mb-4 flex-col w-full">
                    <div className="flex items-center gap-2">
                        <User size={18} />
                        <span>By María González</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Calendar size={18} />
                        <span>Nov 5, 2024</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Clock size={18} />
                        <span>8 min read</span>
                    </div>
                </div>
            </header>

            {/* Main Image */}
            <div className="mb-8">
                <img
                    src={example1}
                    alt="Modern Office Space in Dubai"
                    className="w-full rounded-lg aspect-[21/14]"
                />
                <p className="text-sm text-gray-500 mt-2 text-center">
                    Image: A modern office setup in Dubai
                </p>
            </div>

            {/* Blog Content */}
            <div className="prose lg:prose-xl">
                <p className="text-lg text-gray-700 mb-6">
                    Dubai is one of the most attractive places in the world to start a business, thanks to its thriving economy, strategic location, and favorable business regulations. One of the most convenient and cost-effective ways for entrepreneurs to establish their business in Dubai is through an all-inclusive office lease agreement. In this article, we will discuss what an all-inclusive office lease is, its benefits, and why it is the best option for foreign entrepreneurs looking to set up in Dubai.
                </p>

                {/* Section: What is an All-Inclusive Office Lease Agreement? */}
                <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
                    What is an All-Inclusive Office Lease Agreement?
                </h2>
                <p className="text-lg text-gray-700 mb-6">
                    An all-inclusive office lease agreement in Dubai offers a hassle-free solution for businesses. This type of lease includes not only the office space but also utilities, maintenance, furniture, and additional services such as internet and cleaning. Entrepreneurs can simply move in and start working without worrying about additional expenses or time-consuming administrative tasks.
                </p>

                {/* Secondary Image */}
                <div className="my-8">
                    <img
                        src={example2}
                        alt="Flexible Office Space"
                        className="w-full rounded-lg aspect-[21/14]"
                    />
                    <p className="text-sm text-gray-500 mt-2 text-center">
                        Image: Flexible office space included in all-inclusive leases
                    </p>
                </div>

                {/* Section: Key Benefits */}
                <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
                    Key Benefits of All-Inclusive Office Lease Agreements
                </h2>
                <p className="text-lg text-gray-700 mb-6">
                    Here are the top advantages of choosing an all-inclusive office lease in Dubai:
                </p>

                <ul className="list-disc list-inside space-y-2 text-gray-700 mb-8">
                    <li>
                        <strong>Cost Efficiency:</strong> A single monthly payment covers rent, utilities, maintenance, and furniture, simplifying budgeting and avoiding unexpected costs.
                    </li>
                    <li>
                        <strong>Flexibility:</strong> Easily scale your office space up or down as your business grows, without long-term commitments.
                    </li>
                    <li>
                        <strong>Prime Locations:</strong> Many offices are located in premium business districts, enhancing your professional image.
                    </li>
                    <li>
                        <strong>Hassle-Free Setup:</strong> Move in and start working immediately, without the need to handle logistics or administrative tasks.
                    </li>
                    <li>
                        <strong>Networking Opportunities:</strong> Co-working spaces foster connections with other entrepreneurs and potential business partners.
                    </li>
                </ul>

                {/* Section: How to Secure a Lease */}
                <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
                    How to Secure an All-Inclusive Office Lease in Dubai
                </h2>
                <ol className="list-decimal list-inside space-y-2 text-gray-700 mb-8">
                    <li>
                        <strong>Choose the Right Location:</strong> Select a district that aligns with your business needs, such as free zones or central business hubs.
                    </li>
                    <li>
                        <strong>Research Lease Providers:</strong> Compare providers based on reputation, services offered, and pricing.
                    </li>
                    <li>
                        <strong>Sign the Lease:</strong> Carefully review terms, including included services and flexibility options.
                    </li>
                    <li>
                        <strong>Register Your Business:</strong> Complete trade licensing and visa processes via Dubai’s government portals.
                    </li>
                </ol>

                {/* Call to Action */}
                <div className="mt-8 flex justify-center">
                    <Link
                        to="/"
                        className="bg-blue-600 text-white px-6 py-3 rounded-lg text-xl hover:bg-blue-700 transition duration-300"
                    >
                        Start Your Business in Dubai Today
                    </Link>
                </div>

                {/* Additional Content */}
                <p className="mt-8 text-lg text-gray-700">
                    Dubai’s favorable environment and strategic location make it a top choice for entrepreneurs. To explore more opportunities, check out our <Link to="/" className="text-blue-600">comprehensive guide</Link>.
                </p>
            </div>
        </article>
    );
};

export default BlogTemplate;
